import { useLayoutEffect, useRef } from 'react';

/**
 * Zone à hauteur contrainte qui garantit que TOUT le contenu est visible —
 * jamais de scroll, jamais de troncature :
 * 1. recherche binaire du font-size (~7 itérations entre minSize et maxSize),
 *    le texte hérite, les médias sont bornés par le consommateur ;
 * 2. si ça déborde encore à minSize (grande image/SVG), le contenu entier est
 *    réduit par transform: scale() jusqu'à tenir — le zoom au clic
 *    (ZoomableMedia) compense : un média devenu petit s'agrandit plein écran.
 *
 * Le font-size et le scale sont pilotés impérativement (hors state React) :
 * la mesure exige des allers-retours de style synchrones dans le layout.
 *
 * `fitKey` : identifiant du contenu (n° de question) — refit à chaque changement.
 */
const AutoFitText = ({ minSize = 16, maxSize = 44, fitKey, className = '', children }) => {
  const boxRef = useRef(null);
  const contentRef = useRef(null);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const contenu = contentRef.current;
    if (!box || !contenu) return undefined;

    // Tient en hauteur ET en largeur : une expression insécable plus large
    // que l'écran (htmlAvecKatex) fait rétrécir la police au lieu d'être
    // rognée sur le bord droit.
    const tient = () => box.scrollHeight <= box.clientHeight + 1 && box.scrollWidth <= box.clientWidth + 1;

    const fit = () => {
      contenu.style.transform = '';
      box.style.fontSize = `${maxSize}px`;
      if (tient()) return;

      let lo = minSize;
      let hi = maxSize;
      for (let i = 0; i < 7; i++) {
        const mid = (lo + hi) / 2;
        box.style.fontSize = `${mid}px`;
        if (tient()) lo = mid;
        else hi = mid;
      }
      box.style.fontSize = `${lo}px`;

      // Filet « tout visible » : réduction géométrique du bloc entier
      if (box.scrollHeight > box.clientHeight + 1) {
        const ratio = Math.max(0.3, box.clientHeight / box.scrollHeight);
        contenu.style.transform = `scale(${ratio})`;
        contenu.style.transformOrigin = 'top center';
      }
    };

    fit();
    // Observe le CONTENU (pas seulement la boîte) : un chargement d'image ou
    // un rendu KaTeX tardif change scrollHeight sans redimensionner la boîte.
    // (transform ne modifie pas la taille layout → pas de boucle d'observation)
    const ro = new ResizeObserver(fit);
    ro.observe(box);
    ro.observe(contenu);
    return () => ro.disconnect();
  }, [minSize, maxSize, fitKey]);

  return (
    <div className={`min-h-0 ${className}`}>
      {/* flex + marges auto : centre verticalement une question courte, sans
          risque de clipping quand le contenu remplit la boîte (les marges
          auto tombent à 0, contrairement à justify-center qui rognerait) */}
      <div ref={boxRef} className="h-full overflow-hidden flex flex-col">
        <div ref={contentRef} className="my-auto">{children}</div>
      </div>
    </div>
  );
};

export default AutoFitText;
